import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { DeveloperAuthorContribution } from '@6grain/masofa-monolith-api-client/common';

/** Distinct, readable chip colors keyed by a stable author hash. */
const AUTHOR_PALETTE: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: '#dbeafe', fg: '#1e3a8a' }, // blue
  { bg: '#dcfce7', fg: '#14532d' }, // green
  { bg: '#ffedd5', fg: '#9a3412' }, // orange
  { bg: '#e0e7ff', fg: '#312e81' }, // indigo
  { bg: '#fce7f3', fg: '#9d174d' }, // pink
  { bg: '#ccfbf1', fg: '#115e59' }, // teal
  { bg: '#fef3c7', fg: '#92400e' }, // amber
  { bg: '#e2e8f0', fg: '#1e293b' }, // slate
  { bg: '#fee2e2', fg: '#991b1b' }, // red
  { bg: '#d1fae5', fg: '#065f46' }, // emerald
  { bg: '#e0f2fe', fg: '#0c4a6e' }, // sky
  { bg: '#fae8ff', fg: '#86198f' }, // fuchsia
];

function authorColorKey(author: DeveloperAuthorContribution): string {
  return (author.initials || author.name || '??').trim().toUpperCase();
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function authorChipColors(author: DeveloperAuthorContribution): { bg: string; fg: string } {
  const index = hashString(authorColorKey(author)) % AUTHOR_PALETTE.length;
  return AUTHOR_PALETTE[index];
}

@Component({
  selector: 'app-catalog-authors',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  templateUrl: './catalog-authors.html',
  styleUrl: './catalog-authors.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CatalogAuthorsComponent {
  @Input() authors: DeveloperAuthorContribution[] | null | undefined = [];

  get visibleAuthors(): DeveloperAuthorContribution[] {
    return this.authors ?? [];
  }

  chipStyle(author: DeveloperAuthorContribution): Record<string, string> {
    const colors = authorChipColors(author);
    return {
      'background-color': colors.bg,
      color: colors.fg,
    };
  }

  get tooltipHtml(): string {
    const authors = this.visibleAuthors;
    if (!authors.length) {
      return 'No git authors found for this source file';
    }
    const rows = authors
      .map((author) => {
        const colors = authorChipColors(author);
        const initials = this.escape(author.initials || '??');
        const name = this.escape(author.name ?? '');
        const percent = Number(author.percent ?? 0).toFixed(1);
        return `<tr><td class="initials"><span class="author-chip" style="background-color:${colors.bg};color:${colors.fg}">${initials}</span></td><td>${name}</td><td class="pct">${percent}%</td></tr>`;
      })
      .join('');
    return `<table class="catalog-authors-tooltip"><thead><tr><th></th><th>Author</th><th>%</th></tr></thead><tbody>${rows}</tbody></table>`;
  }

  private escape(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }
}
